import Vue from 'vue'
import { createPaginatedFetcher } from '@/@core/libs/fetch/usePaginatedFetcher'
import { affiliateAxiosIns } from '@/libs/axios'

/**
 * Opsi yang menggunakan paginated fetcher (infinite scroll).
 * Key adalah `option_type`, value adalah fungsi pembuat fetch yang menerima `httpPartner`.
 *
 * @type {Record<string, (httpPartner: AxiosInstance) => (params: object) => Promise<{ data: any[] }>>}
 */
const PAGINATED_OPTION_TYPES = {
  domicile: ({ offset, limit, search }) => affiliateAxiosIns
    .get('/v1/register/search-city', { params: { offset, limit, search } })
    .then(res => ({ data: res.data.data || [] }))
    .catch(() => ({ data: [] })),
}

/**
 * Composable untuk mengambil data opsi pada field form campaign.
 *
 * Mendukung dua mode:
 * - **Biasa**: fetch sekali dari endpoint `/komclass/api/v1/komlabs/campaigns/options`
 * - **Paginated**: untuk `option_type` tertentu (misal: `domicile`), menggunakan
 *   `createPaginatedFetcher` sehingga mendukung infinite scroll.
 *
 * @param {import('axios').AxiosInstance} httpNew - Instance axios untuk API komlabs (campaign options).
 * @param {{ option_type: string }} params - Parameter query, wajib mengandung `option_type`.
 * @param {import('axios').AxiosInstance} [httpPartner] - Instance axios untuk API partner (dibutuhkan untuk paginated types).
 *
 * @returns {Vue} Objek reaktif dengan shape:
 *   - `data: any[]`
 *   - `isLoading: boolean`
 *   - `error: any`
 *   - `hasMore?: boolean`        — hanya pada paginated
 *   - `fetchData?: Function`     — hanya pada paginated
 *   - `search: (query: string) => void` — tersedia pada semua mode
 */
// eslint-disable-next-line import/prefer-default-export
export function useCampaignOptions(httpNew, params = {}) {
  const { option_type: optionType } = params

  // Mode paginated: untuk tipe yang didefinisikan di PAGINATED_OPTION_TYPES
  if (optionType && PAGINATED_OPTION_TYPES[optionType]) {
    const fnFetch = PAGINATED_OPTION_TYPES[optionType]
    const fetcher = createPaginatedFetcher(fnFetch, 0, 50)
    fetcher.fetchData()

    /**
     * Lakukan pencarian server-side: reset data dan fetch ulang dengan query baru.
     * @param {string} query
     */
    fetcher.search = query => {
      fetcher.fetchData({ reset: true, options: { search: query || undefined } })
    }

    return fetcher
  }

  // Mode biasa: fetch sekali dari API campaign options
  const state = Vue.observable({
    data: [],
    isLoading: false,
    error: null,
  })

  const fetchOptions = async (extraParams = {}) => {
    if (!httpNew) {
      return
    }
    state.isLoading = true
    state.error = null
    try {
      const { data } = await httpNew.get('/komclass/api/v1/komlabs/campaigns/options', {
        params: { ...params, ...extraParams },
      })
      state.data = data
    } catch (err) {
      state.error = err?.response?.data?.meta?.message || 'Gagal memuat data'
    } finally {
      state.isLoading = false
    }
  }

  fetchOptions()

  state.fetchOptions = fetchOptions

  /**
   * Lakukan pencarian server-side: refetch dengan parameter `search`.
   * @param {string} query
   */
  state.search = query => {
    fetchOptions(query ? { search: query } : {})
  }

  return state
}
